import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { useDebouncedState } from '../../../hooks/useDebouncedState'
import { twMerge } from 'tailwind-merge'
import { UseState } from '@unimpaired/utils'
import { useClickOutside } from '../../../hooks'

type Props = {
  onSearch: (event: string) => void
  onClick?: () => void
  searchingState?: UseState<boolean>
  placeholder?: string
  autoFocus?: boolean
  debounce?: boolean
  defaultValue?: string
  className?: string
  /** Classes surrounding the input element itself */
  inputClasses?: string
  loading?: boolean
  onLeave?: () => void
}

export const Search = ({ onSearch, className, ...props }: Props) => {
  const placeholder = props.placeholder ? props.placeholder : 'Search'
  const [searching, setSearching] = props.searchingState || useState(false)

  const [debouncedValue, setDebouncedValue, debouncing] =
    useDebouncedState<string>(
      '',
      props.debounce ? 300 : 0,
      () => !!props.debounce,
    )

  useEffect(() => {
    onSearch(debouncedValue)
    setSearching(false)
  }, [debouncedValue])

  const loading = props.loading || debouncing

  const ref = useClickOutside(
    () => {
      props.onLeave?.()
    },
    {},
    // { disableWhen: searching || debouncedValue.length === 0 },
  )

  return (
    <div
      ref={ref}
      className={twMerge(
        `flex w-full bg-base-0 grow dark:bg-transparent border rounded-lg`,
        className,
      )}>
      <div className='md:ml-0 flex w-full'>
        <label htmlFor='search_field' className='sr-only'>
          Search
        </label>
        <div className='focus-within:text-base-600 relative w-full text-base-400'>
          <div className='absolute inset-y-0 left-0 flex items-center ml-2 pointer-events-none'>
            {!loading && !searching && <FontAwesomeIcon icon={faSearch} />}
            {(loading || searching) && (
              <svg
                className='animate-spin -ml-1 mr-3 h-5 w-5 text-blue-800'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'>
                <circle
                  className='opacity-25'
                  cx='12'
                  cy='12'
                  r='10'
                  stroke='currentColor'
                  strokeWidth='4'></circle>
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
              </svg>
            )}
          </div>
          <input
            onFocus={props.onClick}
            id='search_field'
            onChange={(e) => {
              setSearching(true)
              setDebouncedValue(e.target.value)
            }}
            defaultValue={props.defaultValue || ''}
            autoFocus={props.autoFocus}
            autoComplete='off'
            className={twMerge(
              'focus:outline-none focus:placeholder-gray-400 sm:text-sm block w-full h-full py-3 pl-8 pr-3 text-base-900 placeholder-gray-500 dark:placeholder-gray-400 rounded-md font-normal',
              props.inputClasses,
            )}
            placeholder={`${placeholder}`}
            type='search'
          />
        </div>
      </div>
    </div>
  )
}

export const SearchSkeleton = (props: {
  className?: string
  height?: number
  width?: number
}) => (
  <div
    style={{ height: props.height, width: props.width }}
    className={twMerge(
      'bg-slate-200 animate-pulse rounded-md',
      props.className,
    )}></div>
)
